/**
 * Berndorf — Ziehen mit der Maus auf waagrechten Scrollern.
 *
 * Opt-in über `data-berndorf-drag-scroll` am scrollenden Element selbst
 * (nicht am Wrapper). Im Einsatz an der Karussell-Spur und an der
 * Thumbnail-Leiste der Produktgalerie.
 *
 * Touch und Tastatur bleiben unangetastet — dort scrollt der Browser selbst;
 * gezogen wird ausschließlich mit der Maus.
 *
 * Zwei Eigenheiten, die beim Nachbauen sonst fehlen:
 *   - Erst ab 6 px Weg gilt es als Ziehen. Darunter bleibt es ein Klick,
 *     sonst verschluckt jede zittrige Hand den Klick auf Karte oder Thumbnail.
 *   - Nach echtem Ziehen wird der folgende Klick in der Capture-Phase
 *     abgefangen. Ohne das öffnet das Loslassen die Karte, auf der die Maus
 *     gerade liegen bleibt.
 *
 * `is-draggable` sitzt nur, solange wirklich etwas zu scrollen ist — der
 * Greifhand-Cursor hängt daran. Die Thumbnail-Leiste passt meist ohne
 * Überlauf (die meisten Produkte haben zwei bis vier Bilder), ein dauerhafter
 * Greifcursor würde dort etwas versprechen, was es nicht gibt.
 *
 * Formularfelder bleiben ausgenommen: dort zieht man Text, nicht die Reihe.
 * Buttons dagegen sind Ziehfläche wie alles andere — der Klick darauf
 * überlebt, solange die Maus stillhält.
 */
(function () {
  'use strict';

  var IGNORE = 'input, select, textarea, [contenteditable=""], [contenteditable="true"], [role="checkbox"]';
  var THRESHOLD = 6;
  var cleanups = new WeakMap();

  function attach(element, options) {
    if (!element) return function () {};

    var controller = new AbortController();
    var listenerOptions = { signal: controller.signal };
    var drag = null;
    var suppressClick = false;
    var sizeObserver = null;
    var childObserver = null;

    function syncDraggable() {
      element.classList.toggle('is-draggable', element.scrollWidth > element.clientWidth + 1);
    }

    function endDrag(event) {
      if (!drag || (event && event.pointerId !== drag.id)) return;
      var id = drag.id;
      suppressClick = drag.moved;
      drag = null;
      element.classList.remove('is-dragging');
      if (element.hasPointerCapture(id)) element.releasePointerCapture(id);
    }

    element.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 ||
          event.target.closest(IGNORE) ||
          element.scrollWidth <= element.clientWidth) return;
      suppressClick = false;
      drag = { id: event.pointerId, x: event.clientX, scroll: element.scrollLeft, moved: false };
    }, listenerOptions);

    element.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.id) return;
      var distance = event.clientX - drag.x;
      if (!drag.moved && Math.abs(distance) < THRESHOLD) return;
      if (!drag.moved) {
        drag.moved = true;
        element.setPointerCapture(drag.id);
        element.classList.add('is-dragging');
      }
      event.preventDefault();
      element.scrollLeft = drag.scroll - distance;
    }, listenerOptions);

    element.addEventListener('pointerup', endDrag, listenerOptions);
    element.addEventListener('pointercancel', endDrag, listenerOptions);
    element.addEventListener('lostpointercapture', endDrag, listenerOptions);
    element.addEventListener('pointerleave', function (event) {
      if (drag && !drag.moved) endDrag(event);
    }, listenerOptions);
    element.addEventListener('dragstart', function (event) {
      if (drag) event.preventDefault();
    }, listenerOptions);
    element.addEventListener('click', function (event) {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true, signal: controller.signal });

    window.addEventListener('resize', syncDraggable, listenerOptions);
    if (typeof ResizeObserver === 'function') {
      sizeObserver = new ResizeObserver(syncDraggable);
      sizeObserver.observe(element);
    }
    /* Spuren, die erst nachträglich gefüllt werden (zuletzt angesehen), ändern
       ihre eigene Breite nie — nur die ihres Inhalts. Ohne diesen Beobachter
       bliebe der Greifcursor dort aus. */
    if (typeof MutationObserver === 'function') {
      childObserver = new MutationObserver(syncDraggable);
      childObserver.observe(element, { childList: true });
    }
    syncDraggable();

    var detach = function () {
      endDrag();
      if (sizeObserver) sizeObserver.disconnect();
      if (childObserver) childObserver.disconnect();
      controller.abort();
      element.classList.remove('is-draggable');
    };

    /* Der Aufrufer darf mit seinem eigenen Signal aufräumen. */
    if (options && options.signal) {
      options.signal.addEventListener('abort', detach, { once: true });
    }

    return detach;
  }

  function initOne(element) {
    if (element.dataset.berndorfDragScrollReady === 'true') return;
    element.dataset.berndorfDragScrollReady = 'true';
    var detach = attach(element);
    cleanups.set(element, function () {
      detach();
      delete element.dataset.berndorfDragScrollReady;
    });
  }

  function init(root) {
    (root || document).querySelectorAll('[data-berndorf-drag-scroll]').forEach(initOne);
  }

  window.BerndorfDragScroll = { attach: attach, init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-berndorf-drag-scroll]').forEach(function (element) {
      var cleanup = cleanups.get(element);
      if (cleanup) cleanup();
    });
  });
})();
